import React, { useState, useCallback } from 'react';
import MobileScrollInput from './MobileScrollInput';

function CompensationDisplay({ showCheatSheet, setShowCheatSheet, tabs, compensations, getUnitForParam }) {
  const [compensationSteps, setCompensationSteps] = useState([
    { param: '', change: '', corner: '', paramSelected: false, showDropdown: false, suggestions: [] }
  ]);

  // Filter compensation parameter suggestions
  const updateCheatSheetSuggestions = useCallback((inputValue, stepIndex = 0) => {
    setCompensationSteps(prev => prev.map((step, idx) => {
      if (idx !== stepIndex) return step;

      if (!inputValue.trim()) {
        return { ...step, suggestions: [], showDropdown: false };
      }

      const allParams = tabs.flatMap(tab => tab.groups.flatMap(group => group.params));
      const filtered = allParams
        .filter(param => param.name.toLowerCase().includes(inputValue.toLowerCase()))
        .slice(0, 5); // Limit to top 5 results

      return { ...step, suggestions: filtered, showDropdown: filtered.length > 0 };
    }));
  }, [tabs]);

  const addCompensationStep = useCallback(() => {
    setCompensationSteps(prev => [...prev, { param: '', change: '', corner: '', paramSelected: false, showDropdown: false, suggestions: [] }]);
  }, []);

  const removeCompensationStep = useCallback((stepIndex) => {
    if (compensationSteps.length > 1) {
      setCompensationSteps(prev => prev.filter((_, idx) => idx !== stepIndex));
    }
  }, [compensationSteps.length]);

  const updateCompensationStep = useCallback((stepIndex, field, value) => {
    setCompensationSteps(prev => prev.map((step, idx) => {
      if (idx !== stepIndex) return step;
      return { ...step, [field]: value };
    }));
  }, []);

  if (!showCheatSheet) return null;

  return (
    <div className="overlay-card">
      <div className="card-container" style={{ minHeight: '450px' }}>
        <div className="page-title" style={{ paddingBottom: '20px' }}>
          <h1>Compensations</h1>
          <button onClick={() => setShowCheatSheet(false)} className="close-button">✕</button>
        </div>
        {compensationSteps.map((step, stepIndex) => (
          <div key={stepIndex} className="cheat-sheet-section">
            <div className="compensation-step-header">
              <h3>{stepIndex === 0 ? 'I Want To Change...' : `And Then I Want To Change...`}</h3>
              {stepIndex > 0 && (
                <button
                  className="remove-step-button"
                  onClick={() => removeCompensationStep(stepIndex)}
                  title="Remove this step"
                >
                  -
                </button>
              )}
              {stepIndex === compensationSteps.length - 1 && (
                <button
                  className="add-step-button"
                  onClick={addCompensationStep}
                  title="Add another step"
                >
                  +
                </button>
              )}
            </div>
            <div className="search-input-container">
              <input
                value={step.param}
                onChange={e => {
                  updateCompensationStep(stepIndex, 'param', e.target.value);
                  updateCompensationStep(stepIndex, 'corner', ''); // Reset corner when parameter changes
                  updateCompensationStep(stepIndex, 'paramSelected', false); // Reset selected flag when typing
                  updateCheatSheetSuggestions(e.target.value, stepIndex);
                }}
                onFocus={() => {
                  if (step.param.trim()) {
                    updateCheatSheetSuggestions(step.param, stepIndex);
                  }
                }}
                onBlur={() => {
                  // Delay hiding dropdown to allow clicks on suggestions
                  setTimeout(() => updateCompensationStep(stepIndex, 'showDropdown', false), 150);
                }}
                placeholder="Select parameter"
                className="event-editor-input"
              />
              {step.showDropdown && step.suggestions.length > 0 && (
                <div className="search-dropdown">
                  {step.suggestions.map((param, index) => (
                    <div
                      key={param.name}
                      className="search-dropdown-item"
                      onClick={() => {
                        updateCompensationStep(stepIndex, 'param', param.name);
                        updateCompensationStep(stepIndex, 'corner', '');
                        updateCompensationStep(stepIndex, 'paramSelected', true);
                        updateCompensationStep(stepIndex, 'showDropdown', false);
                        updateCompensationStep(stepIndex, 'suggestions', []);
                      }}
                    >
                      {param.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {step.param && step.paramSelected && (() => {
              const selectedParam = tabs.flatMap(tab => tab.groups.flatMap(group => group.params)).find(p => p.name === step.param);
              const unit = getUnitForParam(step.param);
              const firstStepCorner = compensationSteps[0]?.corner;

              if (selectedParam?.isFourCornerGroup) {
                // Only show corner picker for the first step
                if (stepIndex === 0) {
                  return (
                    <div className="cheat-sheet-input-row">
                      <div className="corner-selection-grid">
                        {['FL', 'FR', 'RL', 'RR'].map(corner => (
                          <button
                            key={corner}
                            className={`corner-button ${step.corner === corner ? 'selected' : ''}`}
                            onClick={() => updateCompensationStep(stepIndex, 'corner', corner)}
                          >
                            {corner}
                          </button>
                        ))}
                      </div>
                      {step.corner && (
                        <div className="cheat-sheet-scroll-input">
                          <MobileScrollInput
                            value={step.change}
                            onChange={(value) => updateCompensationStep(stepIndex, 'change', value)}
                            step={selectedParam.step || 0.1}
                            unit={unit}
                            id={`cheat-change-${stepIndex}`}
                          />
                          {unit && <div className="scroll-input-unit">{unit}</div>}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // For subsequent steps, show the corner from first step and the input
                  return (
                    <div className="cheat-sheet-input-row">
                      <div className="corner-display">
                        <span className="corner-label">Corner: <strong>{firstStepCorner || 'Select corner in first step'}</strong></span>
                      </div>
                      {firstStepCorner && (
                        <div className="cheat-sheet-scroll-input">
                          <MobileScrollInput
                            value={step.change}
                            onChange={(value) => updateCompensationStep(stepIndex, 'change', value)}
                            step={selectedParam.step || 0.1}
                            unit={unit}
                            id={`cheat-change-${stepIndex}`}
                          />
                          {unit && <div className="scroll-input-unit">{unit}</div>}
                        </div>
                      )}
                    </div>
                  );
                }
              } else if (selectedParam?.isFrontRearGroup) {
                // Only show front/rear picker for the first step
                if (stepIndex === 0) {
                  return (
                    <div className="cheat-sheet-input-row">
                      <div className="corner-selection-grid">
                        {['F', 'R'].map(pos => (
                          <button
                            key={pos}
                            className={`corner-button ${step.corner === pos ? 'selected' : ''}`}
                            onClick={() => updateCompensationStep(stepIndex, 'corner', pos)}
                          >
                            {pos}
                          </button>
                        ))}
                      </div>
                      {step.corner && (
                        <div className="cheat-sheet-scroll-input">
                          <MobileScrollInput
                            value={step.change}
                            onChange={(value) => updateCompensationStep(stepIndex, 'change', value)}
                            step={selectedParam.step || 0.1}
                            unit={unit}
                            id={`cheat-change-${stepIndex}`}
                          />
                          {unit && <div className="scroll-input-unit">{unit}</div>}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // For subsequent steps, show the position from first step and the input
                  return (
                    <div className="cheat-sheet-input-row">
                      <div className="corner-display">
                        <span className="corner-label">Position: <strong>{firstStepCorner || 'Select position in first step'}</strong></span>
                      </div>
                      {firstStepCorner && (
                        <div className="cheat-sheet-scroll-input">
                          <MobileScrollInput
                            value={step.change}
                            onChange={(value) => updateCompensationStep(stepIndex, 'change', value)}
                            step={selectedParam.step || 0.1}
                            unit={unit}
                            id={`cheat-change-${stepIndex}`}
                          />
                          {unit && <div className="scroll-input-unit">{unit}</div>}
                        </div>
                      )}
                    </div>
                  );
                }
              } else {
                return (
                  <div className="cheat-sheet-scroll-input">
                    <MobileScrollInput
                      value={step.change}
                      onChange={(value) => updateCompensationStep(stepIndex, 'change', value)}
                      step={selectedParam?.step || 0.1}
                      unit={unit}
                      id={`cheat-change-${stepIndex}`}
                      showUnitInline={true}
                    />
                  </div>
                );
              }
            })()}
          </div>
        ))}
        {compensationSteps.every((step, stepIndex) => {
          const selectedParam = tabs.flatMap(tab => tab.groups.flatMap(group => group.params)).find(p => p.name === step.param);
          const firstStepCorner = compensationSteps[0]?.corner;
          const effectiveCorner = stepIndex === 0 ? step.corner : firstStepCorner;
          return step.param && step.paramSelected && step.change && (!(selectedParam?.isFourCornerGroup || selectedParam?.isFrontRearGroup) || effectiveCorner);
        }) && (
          <>
            {compensationSteps.map((step, stepIndex) => {
              const selectedParam = tabs.flatMap(tab => tab.groups.flatMap(group => group.params)).find(p => p.name === step.param);
              const firstStepCorner = compensationSteps[0]?.corner;
              const effectiveCorner = stepIndex === 0 ? step.corner : firstStepCorner;
              const isComplete = step.param && step.paramSelected && step.change && (!(selectedParam?.isFourCornerGroup || selectedParam?.isFrontRearGroup) || effectiveCorner);
              if (!isComplete) return null;

              return (
                <div key={`step-${stepIndex}`}>
                  <div className="cheat-sheet-section">
                    <h3>Step {stepIndex + 1}: Will Affect...</h3>
                    {(() => {
                      const relevantComp = compensations.find(c => c.value.parameter === step.param);
                      if (!relevantComp) return <p>No compensations defined for this parameter.</p>;
                      const changeNum = parseFloat(step.change);
                      const cornerIndex = ['FL', 'FR', 'RL', 'RR'].indexOf(effectiveCorner);
                      return (
                        <ul>
                          {relevantComp.value.compensations.map((comp, idx) => {
                            const compRatio = Array.isArray(comp.ratios) ? comp.ratios[cornerIndex] || comp.ratios[0] : comp.ratio;
                            return <li key={idx}>{comp.param} will change by <strong>{(changeNum * compRatio).toFixed(2)} {getUnitForParam(comp.param)}</strong></li>;
                          })}
                        </ul>
                      );
                    })()}
                  </div>
                  <div className="cheat-sheet-section">
                    <h3>Step {stepIndex + 1}: Which Requires...</h3>
                    {(() => {
                      const relevantComp = compensations.find(c => c.value.parameter === step.param);
                      if (!relevantComp) return <p>No compensations defined for this parameter.</p>;
                      const changeNum = parseFloat(step.change);
                      const cornerIndex = ['FL', 'FR', 'RL', 'RR'].indexOf(effectiveCorner);
                      const ratio = Array.isArray(relevantComp.value.ratios) ? relevantComp.value.ratios[cornerIndex] || relevantComp.value.ratios[0] : relevantComp.value.ratio;
                      const requires = [];
                      // Main adjustment
                      requires.push(
                        <li key="main">
                          {relevantComp.value.adjustedBy}: <strong>{(changeNum * ratio).toFixed(2)} {getUnitForParam(relevantComp.value.adjustedBy)}</strong>
                        </li>
                      );
                      // Secondary adjustments (compensations - apply opposite)
                      relevantComp.value.compensations.forEach((comp, idx) => {
                        const compRatio = Array.isArray(comp.ratios) ? comp.ratios[cornerIndex] || comp.ratios[0] : comp.ratio;
                        const secondaryChange = changeNum * compRatio;
                        const comp2 = compensations.find(c => c.value.parameter === comp.param);
                        if (comp2) {
                          const comp2Ratio = Array.isArray(comp2.value.ratios) ? comp2.value.ratios[cornerIndex] || comp2.value.ratios[0] : comp2.value.ratio;
                          const mechanismAdjust = secondaryChange * comp2Ratio;
                          requires.push(
                            <li key={idx}>
                              {comp2.value.adjustedBy}: <strong>{(-mechanismAdjust).toFixed(2)} {getUnitForParam(comp2.value.adjustedBy)}</strong>
                            </li>
                          );
                        } else {
                          requires.push(
                            <li key={idx}>
                              {comp.param}: <strong>{(-secondaryChange).toFixed(2)} {getUnitForParam(comp.param)}</strong>
                            </li>
                          );
                        }
                      });
                      return <ul>{requires}</ul>;
                    })()}
                  </div>
                </div>
              );
            })}

            {/* Combined Changes - only show when there are multiple steps */}
            {compensationSteps.length > 1 && (
              <div className="cheat-sheet-section">
                <h3>Combined Changes:</h3>
                {(() => {
                  const combined = new Map();

                  compensationSteps.forEach((step, stepIndex) => {
                    if (!step.param || !step.paramSelected || !step.change) return;

                    const relevantComp = compensations.find(c => c.value.parameter === step.param);
                    if (!relevantComp) return;

                    const changeNum = parseFloat(step.change);
                    const firstStepCorner = compensationSteps[0]?.corner;
                    const effectiveCorner = stepIndex === 0 ? step.corner : firstStepCorner;
                    const cornerIndex = ['FL', 'FR', 'RL', 'RR'].indexOf(effectiveCorner);

                    // Main adjustment
                    const ratio = Array.isArray(relevantComp.value.ratios) ? relevantComp.value.ratios[cornerIndex] || relevantComp.value.ratios[0] : relevantComp.value.ratio;
                    const mainKey = relevantComp.value.adjustedBy;
                    const mainValue = changeNum * ratio;
                    combined.set(mainKey, (combined.get(mainKey) || 0) + mainValue);

                    // Secondary adjustments (compensations - apply opposite)
                    relevantComp.value.compensations.forEach((comp) => {
                      const compRatio = Array.isArray(comp.ratios) ? comp.ratios[cornerIndex] || comp.ratios[0] : comp.ratio;
                      const secondaryChange = changeNum * compRatio;
                      const comp2 = compensations.find(c => c.value.parameter === comp.param);
                      if (comp2) {
                        const comp2Ratio = Array.isArray(comp2.value.ratios) ? comp2.value.ratios[cornerIndex] || comp2.value.ratios[0] : comp2.value.ratio;
                        const mechanismAdjust = secondaryChange * comp2Ratio;
                        const key = comp2.value.adjustedBy;
                        combined.set(key, (combined.get(key) || 0) - mechanismAdjust);
                      } else {
                        const key = comp.param;
                        combined.set(key, (combined.get(key) || 0) - secondaryChange);
                      }
                    });
                  });

                  // Convert to list
                  const combinedList = Array.from(combined.entries())
                    .filter(([key, value]) => Math.abs(value) > 0.01) // Filter out very small values
                    .map(([key, value]) => (
                      <li key={key}>
                        {key}: <strong>{value.toFixed(2)} {getUnitForParam(key)}</strong>
                      </li>
                    ));

                  return combinedList.length > 0 ? <ul>{combinedList}</ul> : <p>No combined changes.</p>;
                })()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CompensationDisplay;